import { getErrorMessage } from '@/components/hooks/useGetErrorMessage';
import { useHandleActionItem } from '@/components/hooks/useHandleActionItem';
import { useCoreContext } from '@/core';
import { AccountsEntity } from '@/domain/entities/account';
import { CreateAccountPayload, ResetMfaPayload } from '@/domain/ports/account/payloads';
import {
  ChangeEmailUseCase,
  ChangePasswordUseCase,
  CreateAccountUseCase,
  UpdateAccountUseCase,
} from '@/domain/use-cases/accounts';
import { ResetMfaUseCase } from '@/domain/use-cases/accounts/reset-mfa';
import { UpdateRequiredMfaUseCase } from '@/domain/use-cases/accounts/update-required-mfa';
import yup from '@/yupGlobal';
import { yupResolver } from '@hookform/resolvers/yup';
import { notification } from 'antd';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AccountRepoIml } from '../adapters/repositories';

interface Props {
  detailAccount: AccountsEntity;
}

export enum EmailTypeEnum {
  AUTO_GENERATED = 'AUTO_GENERATED',
  USER_PROVIDED = 'USER_PROVIDED',
}

const useAccountForm = (props: Props) => {
  const { detailAccount } = props;
  const { handleClose } = useHandleActionItem();
  const updateAccount = new UpdateAccountUseCase(new AccountRepoIml());
  const resetMfa = new ResetMfaUseCase(new AccountRepoIml());
  const updateRequiredMfa = new UpdateRequiredMfaUseCase(new AccountRepoIml());
  const createAccount = new CreateAccountUseCase(new AccountRepoIml());
  const changePassword = new ChangePasswordUseCase(new AccountRepoIml());
  const changeEmail = new ChangeEmailUseCase(new AccountRepoIml());
  const { actionGetAll } = useCoreContext();

  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);

  const passwordChangeForm = useForm({
    mode: 'onTouched',
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    resolver: yupResolver(
      yup.object({
        newPassword: yup
          .string()
          .required('New password is required')
          .min(8, 'Password must be at least 8 characters'),
        confirmPassword: yup
          .string()
          .required('Confirm password is required')
          .oneOf([yup.ref('newPassword')], 'Passwords must match'),
      }),
    ),
  });

  const emailChangeForm = useForm({
    mode: 'onTouched',
    defaultValues: {
      newEmail: '',
    },
    resolver: yupResolver(
      yup.object({
        newEmail: yup.string().required('New email is required').email('Invalid email format'),
      }),
    ),
  });

  const formatRoles = useCallback(() => {
    if (Array.isArray(detailAccount.roles) && detailAccount.roles.length > 0) {
      return detailAccount.roles.map((item) => item.id);
    }
    return [];
  }, [detailAccount]);

  const defaultValue = {
    id: '',
    username: '',
    email: '',
    emailType: EmailTypeEnum.AUTO_GENERATED,
    firstName: '',
    middleName: '',
    lastName: '',
    displayName: '',
    phoneNo: '',
    address: '',
    avatarUrl: '',
    dob: '',
    gender: '',
    roles: [],
    isActive: true,
    requiredMfa: true,
  };

  const methodForm = useForm({
    mode: 'onTouched',
    defaultValues: {
      id: detailAccount.id ?? '',
      username: detailAccount.username ?? '',
      email: detailAccount.email ?? '',
      emailType: EmailTypeEnum.AUTO_GENERATED,
      firstName: detailAccount.firstName ?? '',
      middleName: detailAccount.middleName ?? '',
      lastName: detailAccount.lastName ?? '',
      displayName: detailAccount.displayName ?? '',
      phoneNo: detailAccount.phoneNo ?? '',
      address: detailAccount.address ?? '',
      avatarUrl: detailAccount.avatarUrl ?? '',
      dob: detailAccount.dob ?? '',
      gender: detailAccount.gender ?? '',
      roles: formatRoles(),
      isActive: detailAccount.isActive ?? true,
      requiredMfa: detailAccount.requiredMfa ?? true,
    },
    resolver: yupResolver(
      yup.object({
        // username: yup.string().required('Username is required'),
        // email: yup.string().required().email('Invalid email'),
        // firstName: yup.string().required('First name is required'),
        // middleName: yup.string().optional(),
        // lastName: yup.string().optional(),
        // displayName: yup.string().optional(),
        // phoneNo: yup.string().optional(),
        // address: yup.string().optional(),
        // avatarUrl: yup.string().optional(),
        // dob: yup.string().optional(),
        // gender: yup.string().optional(),
        // roles: yup.array().of(yup.string()).optional(),
        // isActive: yup.boolean().optional(),
      }),
    ),
  });

  const handleGetDisplayName = (data: CreateAccountPayload): string => {
    if (!data) return '';
    if (data.displayName) return data.displayName;
    return [data.lastName, data.middleName, data.firstName].filter(Boolean).join(' ').trim() || '';
  };

  const onSubmit = methodForm.handleSubmit(async (data: CreateAccountPayload & { id?: string }) => {
    try {
      if (data.id) {
        const { emailType, ...updateData } = data;
        await updateAccount.update({
          ...updateData,
          id: data.id,
          displayName: handleGetDisplayName(data),
        });
      } else {
        delete data.id;
        await createAccount.create(data);
      }
      notification.success({
        message: 'You already have successfully submitted',
        placement: 'bottomRight',
      });
      handleClose();
      methodForm.reset({
        ...defaultValue,
      });
      actionGetAll();
    } catch (error) {
      notification.error({
        message: 'Error',
        description: getErrorMessage(error),
        placement: 'bottomRight',
      });
    }
  });

  const onResetMfa = methodForm.handleSubmit(async (data: ResetMfaPayload) => {
    try {
      await resetMfa.run(data);
      notification.success({
        message: 'Success',
        description: 'Reset MFA successfully',
        placement: 'bottomRight',
      });
    } catch (error) {
      notification.error({
        message: 'Error',
        description: getErrorMessage(error),
        placement: 'bottomRight',
      });
    }
  });

  const onSwitchRequiredMfa = async (requiredMfa: boolean) => {
    try {
      await updateRequiredMfa.run({
        id: methodForm.getValues('id'),
        requiredMfa,
      });
      handleClose();
      notification.success({
        message: 'Success',
        description: 'Update required MFA successfully',
        placement: 'bottomRight',
      });
    } catch (error) {
      notification.error({
        message: 'Error',
        description: getErrorMessage(error),
        placement: 'bottomRight',
      });
    }
  };

  const onChangePassword = passwordChangeForm.handleSubmit(async (data) => {
    try {
      await changePassword.run({
        id: methodForm.getValues('id'),
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword,
      });
      notification.success({
        message: 'Success',
        description: 'Password changed successfully',
        placement: 'bottomRight',
      });
      passwordChangeForm.reset();
      setIsChangePasswordModalOpen(false);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: getErrorMessage(error),
        placement: 'bottomRight',
      });
    }
  });

  const handleOpenChangePasswordModal = () => {
    setIsChangePasswordModalOpen(true);
  };

  const handleCloseChangePasswordModal = () => {
    setIsChangePasswordModalOpen(false);
    passwordChangeForm.reset();
  };

  const onChangeEmail = emailChangeForm.handleSubmit(async (data) => {
    try {
      await changeEmail.run({
        id: methodForm.getValues('id'),
        newEmail: data.newEmail,
      });
      notification.success({
        message: 'Success',
        description: 'Email changed successfully',
        placement: 'bottomRight',
      });
      emailChangeForm.reset();
      setIsChangeEmailModalOpen(false);
      actionGetAll();
    } catch (error) {
      notification.error({
        message: 'Error',
        description: getErrorMessage(error),
        placement: 'bottomRight',
      });
    }
  });

  const handleOpenChangeEmailModal = () => {
    setIsChangeEmailModalOpen(true);
  };

  const handleCloseChangeEmailModal = () => {
    setIsChangeEmailModalOpen(false);
    emailChangeForm.reset();
  };

  return {
    methodForm,
    onSubmit,
    defaultValue,
    onResetMfa,
    onSwitchRequiredMfa,
    detailAccount,
    passwordChangeForm,
    onChangePassword,
    isChangePasswordModalOpen,
    handleOpenChangePasswordModal,
    handleCloseChangePasswordModal,
    emailChangeForm,
    onChangeEmail,
    isChangeEmailModalOpen,
    handleOpenChangeEmailModal,
    handleCloseChangeEmailModal,
  };
};

export default useAccountForm;
